import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import './Home.css';
import heroBackground from '../../assets/home_us_network-resize.png';

function Home() {
  return (
    <div className="home-page">
      <Helmet>
        <title>United Arborist | Tree Care Contractor Network & Technology Services</title>
        <meta name="description" content="United Arborist connects tree service companies with skilled subcontractors nationwide. Find tree climbers, equipment operators, and CDL drivers. Technology development and business consulting for arborists." />
        <meta name="keywords" content="arborist network, tree service contractors, tree care subcontractors, find tree climbers, hire equipment operators, CDL drivers for tree service, arborist business consulting, tree service technology development, tree removal contractors, tree trimming subcontractors, stump grinding services, debris hauling, arborist staffing" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.unitedarborist.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="United Arborist" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="United Arborist | Tree Care Contractor Network & Technology Services" />
        <meta property="og:description" content="Connect tree service companies with skilled subcontractors nationwide. Technology development and business consulting for the arboricultural industry." />
        <meta property="og:url" content="https://www.unitedarborist.com/" />
        <meta property="og:image" content="https://www.unitedarborist.com/logo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="United Arborist | Tree Care Contractor Network & Technology Services" />
        <meta name="twitter:description" content="Connect tree service companies with skilled subcontractors nationwide. Technology development and business consulting for arborists." />
        <meta name="twitter:image" content="https://www.unitedarborist.com/logo.jpg" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "United Arborist",
            "url": "https://www.unitedarborist.com",
            "logo": "https://www.unitedarborist.com/logo.jpg",
            "description": "Connecting tree service companies with skilled subcontractors. Technology development and business consulting for the arboricultural industry.",
            "sameAs": [],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "url": "https://www.unitedarborist.com/contact"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "serviceType": ["Tree Care Contractor Network", "Technology Development", "Business Consulting"]
          }
        `}</script>
      </Helmet>
      {/* Hero Section */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${heroBackground})` }}
      >
        <div className="hero-overlay">
          <Container>
            <div className="hero-content">
              <h1 className="hero-title">United Arborist Network</h1>
              <p className="hero-subtitle">
                A Free Resource for the Contracting Arboricultural Community
              </p>
              <div className="hero-buttons">
                <Button
                  variant="success"
                  size="lg"
                  as={Link}
                  to="/subcontractor-registration"
                  className="me-2 mb-2"
                >
                  Join as Subcontractor
                </Button>
                <Button
                  variant="outline-light"
                  size="lg"
                  as={Link}
                  to="/company-registration"
                  className="mb-2"
                >
                  Register Your Company
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section py-5">
        <Container>
          <h2 className="text-center mb-5">Why Choose United Arborist?</h2>
          <Row className="g-4">
            <Col xs={12} md={3}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                    </svg>
                  </div>
                  <Card.Title>Established Companies</Card.Title>
                  <Card.Text className="text-muted">
                    Whether you're a large or small business, United Arborist will assist in finding independent contractors that fit your contracting needs.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={3}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L4 8h2l-3 5h2l-3 6h8v3h4v-3h8l-3-6h2l-3-5h2L12 2zm0 2.5L16.5 8h-1.8l2.5 4h-1.5l2.3 4H6l2.3-4H6.8l2.5-4H7.5L12 4.5z"/>
                    </svg>
                  </div>
                  <Card.Title>Tree Climbers</Card.Title>
                  <Card.Text className="text-muted">
                    Climb and work where you want with United Arborist. As the community grows, we will present you with opportunities in your specified areas, when and if available.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={3}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 4h-1V2H7v2H6c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h1v6H4v2h16v-2h-3v-6h1c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM8 4h8v2H8V4zm10 6h-2V8h2v2zm-4 8h-4v-6h4v6z"/>
                    </svg>
                  </div>
                  <Card.Title>Equipment Operators</Card.Title>
                  <Card.Text className="text-muted">
                    Skilled and experienced equipment operators are vital to the industry, United Arborist can help match companies to your specific skill sets.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={3}>
              <Card className="feature-card h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                    </svg>
                  </div>
                  <Card.Title>CDL Drivers</Card.Title>
                  <Card.Text className="text-muted">
                    CDL drivers that can assist in tree care operations are difficult to locate. Find out if we have any available opportunities for you.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Technology & Consulting Section */}
      <section className="tech-consulting-section py-5">
        <Container>
          <Row className="align-items-center g-5">
            <Col xs={12} lg={6}>
              <h2 className="mb-3">Technology Development &amp; Business Consulting</h2>
              <p className="text-muted mb-3">
                Beyond connecting professionals, United Arborist offers technology development and business consulting services tailored to the tree care industry. From custom software and workflow tools to operational strategy and growth planning, we help arboricultural businesses run smarter and scale with confidence.
              </p>
              <ul className="text-muted ps-3 mb-4">
                <li className="mb-2">Custom web applications and industry-specific tools</li>
                <li className="mb-2">Workflow automation and operational efficiency</li>
                <li className="mb-2">Business strategy and growth consulting</li>
                <li className="mb-2">Digital presence and online marketing guidance</li>
              </ul>
              <Button variant="success" size="lg" as={Link} to="/contact">
                Get in Touch
              </Button>
            </Col>
            <Col xs={12} lg={6}>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Card className="tech-consulting-card h-100 border-0 shadow-sm text-center p-3">
                    <Card.Body>
                      <div className="mb-3 text-success">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 3H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h3l-1 1v2h12v-2l-1-1h3c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 13H4V5h16v11z"/>
                        </svg>
                      </div>
                      <Card.Title className="fs-6">Custom Software</Card.Title>
                      <Card.Text className="text-muted small">Tailored tools built for the unique needs of tree care operations.</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="tech-consulting-card h-100 border-0 shadow-sm text-center p-3">
                    <Card.Body>
                      <div className="mb-3 text-success">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
                        </svg>
                      </div>
                      <Card.Title className="fs-6">Business Consulting</Card.Title>
                      <Card.Text className="text-muted small">Strategic guidance to help your arboricultural business grow and compete.</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="tech-consulting-card h-100 border-0 shadow-sm text-center p-3">
                    <Card.Body>
                      <div className="mb-3 text-success">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
                        </svg>
                      </div>
                      <Card.Title className="fs-6">Workflow Automation</Card.Title>
                      <Card.Text className="text-muted small">Streamline scheduling, bidding, and crew management to save time and reduce costs.</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="tech-consulting-card h-100 border-0 shadow-sm text-center p-3">
                    <Card.Body>
                      <div className="mb-3 text-success">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                        </svg>
                      </div>
                      <Card.Title className="fs-6">Digital Presence</Card.Title>
                      <Card.Text className="text-muted small">Build your brand online with websites, SEO, and digital marketing strategies.</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Section */}
      <section className="services-section py-5 bg-light">
        <Container>
          <h2 className="text-center mb-5">Our Services</h2>
          <Row className="g-4">
            <Col xs={6} md={3}>
              <div className="service-item text-center">
                <div className="service-icon mb-2">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z"/>
                  </svg>
                </div>
                <h6>Tree Removal</h6>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="service-item text-center">
                <div className="service-icon mb-2">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.5 5.6L10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zm12 9.8L17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29c-.39-.39-1.02-.39-1.41 0L1.29 18.96c-.39.39-.39 1.02 0 1.41l2.34 2.34c.39.39 1.02.39 1.41 0L16.7 11.05c.39-.39.39-1.02 0-1.41l-2.33-2.35z"/>
                  </svg>
                </div>
                <h6>Tree Trimming</h6>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="service-item text-center">
                <div className="service-icon mb-2">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h6>Stump Grinding</h6>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="service-item text-center">
                <div className="service-icon mb-2">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                  </svg>
                </div>
                <h6>Debris Hauling</h6>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <Row className="align-items-center">
            <Col xs={12} lg={6} className="mb-4 mb-lg-0">
              <h2>Ready to Get Started?</h2>
              <p className="text-muted mb-0">
                Whether you're a tree service professional looking for work opportunities or a company in need of reliable tree services, we're here to help connect you.
              </p>
            </Col>
            <Col xs={12} lg={6} className="text-lg-end">
              <Button
                variant="success"
                size="lg"
                as={Link}
                to="/contact"
                className="me-2 mb-2"
              >
                Contact Us
              </Button>
              <Button
                variant="outline-success"
                size="lg"
                as={Link}
                to="/subcontractor-registration"
                className="mb-2"
              >
                Learn More
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}

export default Home;
